import { useCallback } from 'react';
import { useUserStore } from '@/state/userStore';
import { hasPremiumAccess } from '@/services/entitlements';
import { usePremiumGateStore } from '@/state/premiumGateStore';

export type PlanningIntelligenceFeature =
  | 'trip_cost_confidence'
  | 'compare_options'
  | 'multi_city_feasibility'
  | 'getting_around';

const FEATURE_COPY: Record<
  PlanningIntelligenceFeature,
  { title: string; message: string }
> = {
  trip_cost_confidence: {
    title: 'Trip cost confidence',
    message:
      'Premium unlocks smart cost ranges, breakdown by category, and one-tap budget setup from your estimate.',
  },
  compare_options: {
    title: 'Compare options',
    message:
      'Premium lets you compare 2–3 ideas side-by-side with weighted trade-offs before adding to your trip.',
  },
  multi_city_feasibility: {
    title: 'Multi-city feasibility',
    message:
      'Premium checks city dates, layovers, and connection gaps so multi-stop trips stay realistic.',
  },
  getting_around: {
    title: 'Next up on map',
    message:
      'Premium surfaces leave-now timing and directions to your next activity on the Travel map.',
  },
};

export function usePlanningIntelligencePremium() {
  const profile = useUserStore((s) => s.profile);
  const openPremiumGate = usePremiumGateStore((s) => s.open);

  const isPremium = hasPremiumAccess({
    isPremium: profile?.isPremium,
    planTier: profile?.planTier,
    entitlement: profile?.entitlement,
  });

  const requirePremium = useCallback(
    (feature: PlanningIntelligenceFeature): boolean => {
      if (isPremium) return true;
      const copy = FEATURE_COPY[feature];
      openPremiumGate({
        feature,
        title: copy.title,
        message: copy.message,
        source: 'feature_gate',
      });
      return false;
    },
    [isPremium, openPremiumGate],
  );

  return { isPremium, requirePremium };
}
